import { Frown, Info, MessageCircleQuestion } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Label, PolarRadiusAxis, RadialBar, RadialBarChart } from 'recharts';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/Domain/components/Shared/Card/Card';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '@/Domain/components/Shared/Chart/chart';
import { ChartPlaceholder } from '@/Domain/components/Shared/ChartPlaceholder';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/Domain/components/Shared/Tooltip/Tooltip';
import { RESOLUTION_STATUS_COLORS } from '@/Domain/hooks/Analytics/resolutionStatusColors';
import { toKey } from '@/Domain/hooks/Analytics/useConversationAnalytics.hook';

type Props = {
  totals: Record<string, number>;
  labels: readonly string[];
  isLoading: boolean;
};

const RADIAL_GAP_RATIO = 0.006;
const SMALL_TALK_KEY = 'small_talk';
const RESOLVED_KEYS = new Set(['confirmed_resolution', 'assumed_resolution', 'resolved']);

export const ResolutionBreakdownCard = ({ totals, labels, isLoading }: Props) => {
  const { t } = useTranslation(['dashboard', 'insights', 'insights_page']);

  const items = useMemo(
    () =>
      labels
        .map((label) => {
          const key = toKey(label);
          return {
            key,
            title: t(`insights_page:ai_insights.conversation_outcome.${key}`, label),
            count: totals[key] ?? 0,
            color: RESOLUTION_STATUS_COLORS[key] ?? 'hsl(220 9% 46%)',
          };
        })
        .filter((item) => item.key !== SMALL_TALK_KEY),
    [labels, totals, t],
  );
  const smallTalk = totals[SMALL_TALK_KEY] ?? 0;

  const total = useMemo(() => items.reduce((sum, item) => sum + item.count, 0), [items]);
  const hasData = total > 0;
  const hasNotCounted = smallTalk > 0;

  const totalResolved = items
    .filter((item) => RESOLVED_KEYS.has(item.key))
    .reduce((sum, item) => sum + item.count, 0);
  const resolutionRate = total > 0 ? (totalResolved / total) * 100 : 0;

  const visibleItems = useMemo(() => items.filter((item) => item.count > 0), [items]);

  // RadialBarChart needs a single data row with each status as a separate key.
  // Gap entries (_gap0, _gap1, …) create visual spacing between segments.
  const chartData = useMemo(() => {
    const gapSize = total * RADIAL_GAP_RATIO;
    const data: Record<string, number> = {};
    visibleItems.forEach((item, i) => {
      data[item.key] = item.count;
      if (i < visibleItems.length - 1) {
        data[`_gap${i}`] = gapSize;
      }
    });
    return [data];
  }, [visibleItems, total]);

  const chartConfig = useMemo(
    () =>
      Object.fromEntries(items.map((item) => [item.key, { label: item.title, color: item.color }])),
    [items],
  );

  return (
    <Card
      className={`pt-0 ${isLoading ? 'animate-pulse' : ''}`}
      data-testid="resolution-breakdown-card"
    >
      <CardHeader>
        <CardTitle>
          {t('dashboard:conversations.resolution_breakdown', 'Resolution Breakdown')}
        </CardTitle>
        <CardDescription data-testid="resolution-counted-description">
          {t('insights:analytics.resolution_counted_description')}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {!hasData ? (
          <div data-testid="resolution-empty-state">
            <ChartPlaceholder
              icon={hasNotCounted ? MessageCircleQuestion : Frown}
              titleTranslationKey={
                hasNotCounted ? 'no_customer_questions.title' : 'no_data_in_range.title'
              }
              descriptionTranslationKey={
                hasNotCounted ? 'no_customer_questions.description' : 'no_data_in_range.description'
              }
              spacing="small"
            />
          </div>
        ) : (
          <ChartContainer config={chartConfig} className="mx-auto w-full h-52">
            <RadialBarChart data={chartData} endAngle={180} innerRadius={80} outerRadius={130}>
              <ChartTooltip
                cursor={false}
                content={(props) => (
                  <ChartTooltipContent
                    {...props}
                    hideLabel
                    payload={props.payload?.filter((p) => !String(p.dataKey).startsWith('_gap'))}
                  />
                )}
              />
              <PolarRadiusAxis axisLine={false} tick={false} tickLine={false}>
                <Label
                  content={({ viewBox }) => {
                    if (viewBox && 'cx' in viewBox && 'cy' in viewBox) {
                      return (
                        <text textAnchor="middle" x={viewBox.cx} y={viewBox.cy}>
                          <tspan
                            className="fill-foreground text-2xl font-medium"
                            x={viewBox.cx}
                            y={(viewBox.cy || 0) - 16}
                            data-testid="resolution-rate"
                          >
                            {Math.round(resolutionRate)}%
                          </tspan>
                          <tspan
                            className="fill-muted-foreground"
                            x={viewBox.cx}
                            y={(viewBox.cy || 0) + 4}
                          >
                            {t('insights:analytics.resolution_resolved_of_counted', {
                              resolved: totalResolved.toLocaleString(),
                              counted: total.toLocaleString(),
                            })}
                          </tspan>
                        </text>
                      );
                    }
                  }}
                />
              </PolarRadiusAxis>
              {visibleItems.flatMap((item, i) => [
                <RadialBar
                  key={item.key}
                  className="stroke-transparent stroke-2"
                  cornerRadius={5}
                  dataKey={item.key}
                  fill={`var(--color-${item.key})`}
                  stackId="a"
                />,
                ...(i < visibleItems.length - 1
                  ? [
                      <RadialBar
                        key={`_gap${i}`}
                        cornerRadius={0}
                        dataKey={`_gap${i}`}
                        fill="var(--card)"
                        stackId="a"
                      />,
                    ]
                  : []),
              ])}
            </RadialBarChart>
          </ChartContainer>
        )}
      </CardContent>
      {(hasData || hasNotCounted) && (
        <CardFooter className={`flex-col items-stretch gap-4 ${hasData ? '-mt-14' : ''}`}>
          {hasData && (
            <div
              className="grid w-full grid-cols-2 gap-x-4 gap-y-3"
              data-testid="resolution-legend"
            >
              {[...items].reverse().map((item) => {
                const pct = ((item.count / total) * 100).toFixed(1);
                return (
                  <div
                    key={item.key}
                    className="flex flex-col"
                    data-testid="resolution-legend-item"
                  >
                    <div className="flex items-center gap-1.5 mb-0.5">
                      <span
                        className="size-2 rounded-full shrink-0"
                        style={{ backgroundColor: item.color }}
                      />
                      <span className="text-muted-foreground text-xs">{item.title}</span>
                    </div>
                    <span className="text-sm font-medium tabular-nums pl-3.5">
                      {item.count.toLocaleString()} ({pct}%)
                    </span>
                  </div>
                );
              })}
            </div>
          )}
          {hasNotCounted && (
            <div className="space-y-2 border-t pt-3" data-testid="resolution-not-counted">
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <span data-testid="resolution-not-counted-label">
                  {t('insights:analytics.resolution_not_counted')}
                </span>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger
                      aria-label={t('insights:analytics.resolution_not_counted_info_aria')}
                      className="inline-flex rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    >
                      <Info className="size-3.5 cursor-help text-foreground-subtle transition-colors hover:text-muted-foreground" />
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      {t('insights:analytics.resolution_not_counted_tooltip')}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <div
                className="flex items-center justify-between gap-2 text-sm"
                data-testid="resolution-not-counted-small_talk"
              >
                <span className="flex items-center gap-1.5 text-muted-foreground">
                  <span
                    className="size-2 rounded-full shrink-0"
                    style={{ backgroundColor: RESOLUTION_STATUS_COLORS[SMALL_TALK_KEY] }}
                  />
                  {t('insights:analytics.resolution_small_talk')}
                </span>
                <span className="font-medium tabular-nums">{smallTalk.toLocaleString()}</span>
              </div>
            </div>
          )}
        </CardFooter>
      )}
    </Card>
  );
};
